import { useState, useEffect } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  Bot, 
  Palette, 
  Save, 
  Building2, 
  Zap,
  Globe,
  MessageSquare,
  Sparkles,
  Calendar,
  ShieldCheck,
  Smartphone,
  Moon,
  Hand,
  Power,
  AlertCircle,
  Settings,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Wifi,
  WifiOff,
  RefreshCw,
  Info
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantConfigPage = () => {
  const { tenant, tenantSlug, refreshTenantData } = useTenant();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');
  const [bots, setBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState('disconnected'); // disconnected | connecting | connected
  const [whatsappInfo, setWhatsappInfo] = useState(null);

  const [config, setConfig] = useState({
    name: '',
    description: '',
    prompt: '',
    primary_color: '#3b82f6',
    hours: {
      monday: { open: '08:00', close: '18:00', active: true },
      tuesday: { open: '08:00', close: '18:00', active: true },
      wednesday: { open: '08:00', close: '18:00', active: true },
      thursday: { open: '08:00', close: '18:00', active: true },
      friday: { open: '08:00', close: '18:00', active: true },
      saturday: { open: '09:00', close: '14:00', active: true },
      sunday: { open: '00:00', close: '00:00', active: false },
    },
    autoreply_enabled: true,
    logo_url: '',
    closed_message: '',
    welcome_message: ''
  });

  useEffect(() => {
    fetchConfig();
  }, [tenantSlug]);

  const getHeaders = () => {
    const token = localStorage.getItem(`fluxoco_tenant_${tenantSlug}_token`);
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/tenants/${tenantSlug}/config`, getHeaders());
      if (response.data) {
        setConfig(prev => {
          // Si el backend trae 'hours' pero está vacío, usamos el prev.hours para que no desaparezca la UI
          const mergedHours = (response.data.hours && Object.keys(response.data.hours).length > 0) 
            ? response.data.hours 
            : prev.hours;

          return { 
            ...prev, 
            ...response.data,
            hours: mergedHours,
            // Guarantee that if the API sends empty, we fall back to what's already on the screen
            logo_url: response.data.logo_url || tenant?.logo_url || '',
            primary_color: response.data.primary_color || tenant?.primary_color || '#3b82f6'
          };
        });
        if (response.data.bots && response.data.bots.length > 0) {
          setBots(response.data.bots);
          setSelectedBotId(response.data.bots[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      // fallback to tenant data if config endpoint not ready
      if (tenant) {
        setConfig(prev => ({
          ...prev,
          name: tenant.name,
          description: tenant.description || '',
          primary_color: tenant.primary_color || '#3b82f6',
          logo_url: tenant.logo_url || '',
          website_url: tenant.website_url || '',
          tracking_id: tenant.tracking_id || ''
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Global tenant config
      await axios.put(`${API_URL}/api/v1/tenants/${tenantSlug}/config`, {
        name: config.name,
        description: config.description,
        primary_color: config.primary_color,
        logo_url: config.logo_url,
        website_url: config.website_url,
        tracking_id: config.tracking_id,
        hours: config.hours,
        autoreply_enabled: config.autoreply_enabled,
        closed_message: config.closed_message
      }, getHeaders());

      // If we are in bot tab, save bot specific config
      if (selectedBotId && (activeTab === 'bot' || activeTab === 'advanced')) {
        const selectedBot = bots.find(b => b.id === selectedBotId);
        await axios.put(`${API_URL}/api/v1/tenants/${tenantSlug}/bots/${selectedBotId}`, {
          system_prompt: selectedBot.system_prompt,
          welcome_message: selectedBot.welcome_message
        }, getHeaders());
      }

      toast.success('Configuración guardada correctamente');
      
      // Refresh global tenant data to apply changes everywhere instantly
      if (refreshTenantData) {
        await refreshTenantData();
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const updateBotField = (field, value) => {
    setBots(prev => prev.map(b => b.id === selectedBotId ? { ...b, [field]: value } : b));
  };

  const currentBot = bots.find(b => b.id === selectedBotId) || {};

  const updateHour = (day, field, value) => {
    setConfig(prev => ({
      ...prev,
      hours: {
        ...prev.hours,
        [day]: { ...prev.hours[day], [field]: value }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 rounded-full border-4 border-white/20 animate-spin" 
             style={{ borderTopColor: 'var(--tenant-primary)' }} />
      </div>
    );
  }

  const tabs = [
    { id: 'branding', label: 'Identidad', icon: Building2 },
    { id: 'bot', label: 'Cerebro IA', icon: Bot },
    { id: 'hours', label: 'Horarios', icon: Clock },
    { id: 'advanced', label: 'Automatización', icon: Zap },
    { id: 'whatsapp', label: 'WhatsApp', icon: Smartphone },
  ];

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8 pb-20 w-full"
    >
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 print:hidden">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <div className="flex items-center gap-2 mb-3">
            <Settings className="w-5 h-5 text-yellow-400" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-yellow-500/80">Configuración Core</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit">
            Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-lime-500">Autogestión</span>
          </h1>
          <p className="text-slate-400 mt-2 font-medium italic text-sm">Configura el comportamiento y estética de tu maquinaria de IA.</p>
        </motion.div>

        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="flex items-center gap-2 px-6 py-6 rounded-2xl font-bold transition-all hover:scale-105 active:scale-95 shadow-xl shadow-yellow-500/10"
          style={{ 
            background: `linear-gradient(135deg, #facc15, #84cc16)`,
            color: '#000',
            opacity: saving ? 0.7 : 1
          }}
        >
          {saving ? <div className="w-4 h-4 border-2 border-black/20 border-t-black animate-spin rounded-full" /> : <Save className="w-5 h-5" />}
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Navigation Tabs */}
        <div className="lg:col-span-1 space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-6 py-4 rounded-2xl transition-all ${
                activeTab === tab.id 
                ? 'text-black shadow-lg shadow-yellow-500/20 scale-[1.02]' 
                : 'text-slate-400 hover:bg-slate-900/50 hover:text-white'
              }`}
              style={activeTab === tab.id ? { 
                background: 'linear-gradient(135deg, #facc15, #84cc16)',
              } : {}}
            >
              <tab.icon className="w-5 h-5 font-bold" />
              <span className="font-bold text-sm tracking-tight">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === 'branding' && (
                <Card className="glass-panel rounded-[2.5rem] border-slate-800 p-8">
                  <CardHeader className="px-0 pt-0">
                    <CardTitle className="text-2xl font-outfit text-white">Identidad del Negocio</CardTitle>
                    <CardDescription className="text-slate-400 italic text-sm">Define cómo se presenta tu marca ante los clientes.</CardDescription>
                  </CardHeader>
                  <CardContent className="px-0 space-y-6">
                    <div className="space-y-2">
                       <Label className="text-slate-400">Nombre Público</Label>
                       <Input 
                         value={config.name}
                         onChange={(e) => setConfig({...config, name: e.target.value})}
                         className="h-14 bg-slate-900/50 border-white/10 rounded-xl focus:ring-0 focus:border-yellow-500/40 focus:shadow-[0_0_20px_rgba(250,204,21,0.1)] transition-all duration-300"
                         placeholder="Ej. EBox"
                       />
                     </div>
                      <div className="space-y-2">
                        <Label className="text-slate-400">Descripción / Eslogan</Label>
                        <Input 
                          value={config.description}
                          onChange={(e) => setConfig({...config, description: e.target.value})}
                          className="h-14 bg-slate-900/50 border-white/10 rounded-xl focus:ring-0 focus:border-yellow-500/40 focus:shadow-[0_0_20px_rgba(250,204,21,0.1)] transition-all duration-300"
                          placeholder="Ej. Todo lo que necesitas en una caja"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-400">URL del Logo (Opcional)</Label>
                        <div className="flex gap-4">
                          <Input 
                            value={config.logo_url}
                            onChange={(e) => setConfig({...config, logo_url: e.target.value})}
                            className="h-14 bg-slate-900/50 border-white/10 rounded-xl flex-1"
                            placeholder="https://tu-sitio.com/logo.png"
                          />
                          {config.logo_url && (
                             <div className="w-14 h-14 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center overflow-hidden">
                               <img src={config.logo_url} alt="Logo Preview" className="max-w-full max-h-full object-contain" />
                             </div>
                          )}
                        </div>
                      </div>
                    <div className="space-y-6">
                      <div className="space-y-4">
                        <Label className="text-slate-400 text-sm font-bold uppercase tracking-wider">Identidad Visual Premium</Label>
                        <div className="flex flex-wrap items-center gap-3">
                          {[
                            { name: 'Emerald', value: '#10b981' },
                            { name: 'Cyan', value: '#06b6d4' },
                            { name: 'Electric', value: '#3b82f6' },
                            { name: 'Indigo', value: '#6366f1' },
                            { name: 'Violet', value: '#8b5cf6' },
                            { name: 'Pink', value: '#ec4899' },
                            { name: 'Red', value: '#ef4444' },
                            { name: 'Orange', value: '#f97316' },
                            { name: 'Amber', value: '#f59e0b' },
                            { name: 'Lime', value: '#84cc16' }
                          ].map((color) => (
                            <button
                              key={color.value}
                              onClick={() => setConfig({...config, primary_color: color.value})}
                              className={`group relative w-10 h-10 rounded-full transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center ${
                                config.primary_color === color.value 
                                  ? 'ring-2 ring-white ring-offset-4 ring-offset-slate-950 scale-110 shadow-[0_0_20px_rgba(255,255,255,0.2)]' 
                                  : 'opacity-60 hover:opacity-100'
                              }`}
                              style={{ backgroundColor: color.value }}
                              title={color.name}
                            >
                              {config.primary_color === color.value && (
                                <span className="absolute inset-0 rounded-full border-2 border-white/20 animate-ping"></span>
                              )}
                            </button>
                          ))}
                          {/* Custom Color Picker */}
                          <div className="relative group">
                            <input
                              type="color"
                              value={config.primary_color}
                              onChange={(e) => setConfig({...config, primary_color: e.target.value})}
                              className="w-10 h-10 rounded-full border-0 cursor-pointer bg-transparent overflow-hidden"
                            />
                            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                               <p className="text-[9px] text-slate-500 font-bold uppercase">Custom</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-900/50 border border-white/5 transition-all duration-500 shadow-xl"
                             style={{ borderLeft: `4px solid ${config.primary_color || '#facc15'}` }}>
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg transform transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110"
                               style={{ background: `linear-gradient(135deg, ${config.primary_color || '#facc15'}, transparent)` }}>
                            <Building2 className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 font-medium tracking-wider uppercase mb-1">Tu Identidad de Marca</p>
                            <p className="font-mono text-sm text-white font-bold uppercase tracking-widest">{config.primary_color}</p>
                          </div>
                          <div className="ml-auto flex gap-2">
                             <div className="px-3 py-1 bg-white/5 rounded-full text-xs text-white/50 border border-white/5">Preview</div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-800/50 mt-4">
                        <div className="space-y-2">
                           <Label className="text-slate-400">Sitio Web</Label>
                           <Input 
                            value={config.website_url || ''}
                            onChange={(e) => setConfig({...config, website_url: e.target.value})}
                            placeholder="https://"
                            className="h-14 bg-slate-900/50 border-white/5 rounded-xl transition-all duration-300 focus:ring-0 focus:shadow-[0_0_20px_rgba(255,255,255,0.05)]"
                          />
                        </div>
                        <div className="space-y-2">
                           <Label className="text-slate-400">ID de Seguimiento (Opcional)</Label>
                           <Input 
                            value={config.tracking_id || ''}
                            onChange={(e) => setConfig({...config, tracking_id: e.target.value})}
                            placeholder="G-XXXXXXXX"
                            className="h-14 bg-slate-900/50 border-white/5 rounded-xl transition-all duration-300 focus:ring-0 focus:shadow-[0_0_20px_rgba(255,255,255,0.05)]"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {activeTab === 'bot' && (
                <Card className="glass-panel rounded-[2.5rem] border-slate-800 p-8">
                  <CardHeader className="px-0 pt-0">
                    <CardTitle className="text-2xl font-outfit text-white">Configuración del Agente IA</CardTitle>
                    <CardDescription className="text-slate-400 italic text-sm">Ajusta la personalidad y el conocimiento de tu bot.</CardDescription>
                  </CardHeader>
                  <CardContent className="px-0 space-y-6">
                    {bots.length > 1 && (
                      <div className="p-4 rounded-2xl bg-white/5 border border-white/10 mb-4 animate-in fade-in slide-in-from-top-2">
                        <Label className="text-[10px] text-slate-500 uppercase font-black tracking-widest mb-2 block">Seleccionar Asistente para configurar</Label>
                        <Select value={selectedBotId} onValueChange={setSelectedBotId}>
                          <SelectTrigger className="h-12 bg-slate-900 border-white/5 rounded-xl">
                            <SelectValue placeholder="Elegir bot" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-white/10">
                            {bots.map(b => (
                              <SelectItem key={b.id} value={b.id} className="text-white hover:bg-white/5">{b.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-slate-400">System Prompt (Personalidad de {currentBot.name || 'Bot'})</Label>
                        <span className="text-[10px] bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full font-bold">GPT-4o POWERED</span>
                      </div>
                       <Textarea 
                         value={currentBot.system_prompt || ''}
                         onChange={(e) => updateBotField('system_prompt', e.target.value)}
                         className="min-h-[250px] bg-slate-900/50 border-white/10 rounded-2xl p-6 leading-relaxed resize-none focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300"
                         placeholder="Eres un asistente servicial para Hielo Rey. Tu tono es amable y eficiente..."
                       />
                      <p className="text-[11px] text-slate-500 italic mt-2">
                        Tip: Describe detalladamente cómo quieres que salude, qué información debe priorizar y qué palabras no debe usar.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {activeTab === 'hours' && (
                <Card className="glass-panel rounded-[2.5rem] border-slate-800 p-8">
                  <CardHeader className="px-0 pt-0">
                    <CardTitle className="text-2xl font-outfit text-white">Horarios de Atención</CardTitle>
                    <CardDescription className="text-slate-400 italic text-sm">Define cuándo debe el bot tomar pedidos y cuándo informar que están cerrados.</CardDescription>
                  </CardHeader>
                  <CardContent className="px-0 space-y-4">
                    {/* Live Status Indicator */}
                    {(() => {
                      const now = new Date();
                      const dayKeys = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
                      const todayKey = dayKeys[now.getDay()];
                      const todayConfig = config.hours[todayKey];
                      const currentTime = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
                      const isOpen = todayConfig?.active && currentTime >= (todayConfig?.open || '00:00') && currentTime <= (todayConfig?.close || '23:59');
                      return (
                        <div className={`flex items-center gap-3 p-4 rounded-2xl border transition-all ${
                          isOpen 
                            ? 'bg-emerald-500/10 border-emerald-500/20' 
                            : 'bg-red-500/10 border-red-500/20'
                        }`}>
                          <div className={`w-3 h-3 rounded-full animate-pulse ${
                            isOpen ? 'bg-emerald-400' : 'bg-red-400'
                          }`} />
                          <span className={`text-sm font-bold ${isOpen ? 'text-emerald-400' : 'text-red-400'}`}>
                            {isOpen ? 'Tu negocio está ABIERTO ahora' : 'Tu negocio está CERRADO ahora'}
                          </span>
                          <span className="text-[10px] text-slate-500 ml-auto">
                            {now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      );
                    })()}

                    {/* Day Schedule Grid */}
                    {Object.entries(config.hours).map(([day, val]) => {
                      const dayNames = {
                        monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
                        thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo'
                      };
                      const dayShort = {
                        monday: 'Lun', tuesday: 'Mar', wednesday: 'Mié',
                        thursday: 'Jue', friday: 'Vie', saturday: 'Sáb', sunday: 'Dom'
                      };
                      return (
                        <div key={day} className={`flex items-center justify-between p-4 rounded-2xl border group transition-all duration-300 ${
                          val.active 
                            ? 'bg-slate-900/30 border-slate-800/50 hover:bg-slate-900/60' 
                            : 'bg-slate-950/30 border-slate-900/30 opacity-60'
                        }`}>
                          <div className="flex items-center gap-4 w-36">
                            <Switch 
                              checked={val.active} 
                              onCheckedChange={(checked) => updateHour(day, 'active', checked)}
                            />
                            <div>
                              <span className="font-bold text-sm text-white block">{dayShort[day]}</span>
                              <span className="text-[10px] text-slate-600 hidden md:block">{dayNames[day]}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 flex-1 justify-end">
                            {val.active ? (
                              <>
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] text-slate-500 uppercase font-bold">Abre</span>
                                  <Input 
                                    type="time"
                                    value={val.open}
                                    onChange={(e) => updateHour(day, 'open', e.target.value)}
                                    className="w-32 h-10 bg-slate-800/50 border-slate-700/50 rounded-lg text-xs"
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] text-slate-500 uppercase font-bold">Cierra</span>
                                  <Input 
                                    type="time"
                                    value={val.close}
                                    onChange={(e) => updateHour(day, 'close', e.target.value)}
                                    className="w-32 h-10 bg-slate-800/50 border-slate-700/50 rounded-lg text-xs"
                                  />
                                </div>
                              </>
                            ) : (
                              <span className="text-xs text-slate-600 italic flex items-center gap-2">
                                <Moon className="w-3.5 h-3.5" /> Día de descanso
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {activeTab === 'advanced' && (
                <div className="space-y-6">
                  {/* Auto-respuesta Card */}
                  <Card className="glass-panel rounded-[2.5rem] border-slate-800 p-8">
                    <CardHeader className="px-0 pt-0">
                      <CardTitle className="text-2xl font-outfit text-white">Automatización Inteligente</CardTitle>
                      <CardDescription className="text-slate-400 italic text-sm">Controla el flujo automático de tus conversaciones.</CardDescription>
                    </CardHeader>
                    <CardContent className="px-0 space-y-6 pt-2">
                      <div className="flex items-center justify-between p-6 rounded-[2rem] bg-gradient-to-br from-yellow-400/10 to-lime-500/10 border border-yellow-500/20">
                         <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-yellow-500 flex items-center justify-center shadow-lg shadow-yellow-500/20">
                               <Power className="w-7 h-7 text-black" />
                            </div>
                            <div>
                              <p className="font-bold text-white">Auto-respuesta Inteligente</p>
                              <p className="text-xs text-slate-400">Permitir que la IA responda automáticamente a los clientes.</p>
                            </div>
                         </div>
                         <Switch 
                          checked={config.autoreply_enabled}
                          onCheckedChange={(checked) => setConfig({...config, autoreply_enabled: checked})}
                         />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Mensaje de Bienvenida */}
                  <Card className="glass-panel rounded-[2.5rem] border-slate-800 p-8">
                    <CardContent className="px-0 pt-0 space-y-4">
                      <div className="flex items-center gap-4 mb-2">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
                             style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                          <Hand className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-white text-lg">Mensaje de Bienvenida</p>
                          <p className="text-xs text-slate-400">Lo primero que tu bot dice cuando alguien te contacta por primera vez.</p>
                        </div>
                      </div>
                      <Textarea 
                        value={currentBot.welcome_message || ''}
                        onChange={(e) => updateBotField('welcome_message', e.target.value)}
                        className="min-h-[100px] bg-slate-900/50 border-white/10 rounded-2xl p-5 leading-relaxed resize-none focus:ring-0 focus:border-yellow-500/40 focus:shadow-[0_0_20px_rgba(250,204,21,0.1)] transition-all duration-300"
                        placeholder="¡Hola! 👋 Bienvenido a [tu negocio]. ¿En qué puedo ayudarte hoy?"
                      />
                      <p className="text-[11px] text-slate-500 italic">
                        Tip: Usa emojis y un tono cálido. Este mensaje se envía solo la primera vez que un cliente te escribe.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Mensaje Fuera de Horario */}
                  <Card className="glass-panel rounded-[2.5rem] border-slate-800 p-8">
                    <CardContent className="px-0 pt-0 space-y-4">
                      <div className="flex items-center gap-4 mb-2">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
                             style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                          <Moon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-white text-lg">Mensaje Fuera de Horario</p>
                          <p className="text-xs text-slate-400">Lo que tu bot responde cuando te contactan fuera de tu horario de atención.</p>
                        </div>
                      </div>
                      <Textarea 
                        value={config.closed_message || ''}
                        onChange={(e) => setConfig({...config, closed_message: e.target.value})}
                        className="min-h-[100px] bg-slate-900/50 border-white/10 rounded-2xl p-5 leading-relaxed resize-none focus:ring-0 focus:border-amber-500/40 focus:shadow-[0_0_20px_rgba(245,158,11,0.15)] transition-all duration-300"
                        placeholder="Gracias por contactarnos. En este momento estamos fuera de horario, pero te responderemos lo antes posible. 🕐"
                      />
                      <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/10">
                        <p className="text-[11px] text-amber-200/60 flex items-center gap-2">
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                          Este mensaje se activa automáticamente según los horarios que configuraste arriba.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {activeTab === 'whatsapp' && (
                <div className="space-y-6">
                  {/* Connection Status Hero */}
                  <Card className="glass-panel rounded-[2.5rem] border-slate-800 p-8 overflow-hidden relative">
                    {/* Decorative glow */}
                    <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full blur-[80px] opacity-20"
                         style={{ background: whatsappStatus === 'connected' ? '#25D366' : '#facc15' }} />
                    <CardHeader className="px-0 pt-0 relative z-10">
                      <CardTitle className="text-2xl font-outfit text-white">Conexión WhatsApp Business</CardTitle>
                      <CardDescription className="text-slate-400 italic text-sm">Vincula tu número de WhatsApp Business para activar tu bot de IA.</CardDescription>
                    </CardHeader>
                    <CardContent className="px-0 space-y-6 relative z-10">
                      {/* Status Indicator */}
                      <div className={`flex items-center gap-4 p-6 rounded-[2rem] border transition-all duration-500 ${
                        whatsappStatus === 'connected'
                          ? 'bg-emerald-500/10 border-emerald-500/20'
                          : whatsappStatus === 'connecting'
                          ? 'bg-yellow-500/10 border-yellow-500/20'
                          : 'bg-slate-900/50 border-slate-700/30'
                      }`}>
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-all duration-500 ${
                          whatsappStatus === 'connected'
                            ? 'bg-gradient-to-br from-emerald-400 to-green-600 shadow-emerald-500/30'
                            : whatsappStatus === 'connecting'
                            ? 'bg-gradient-to-br from-yellow-400 to-amber-600 shadow-yellow-500/30 animate-pulse'
                            : 'bg-gradient-to-br from-slate-600 to-slate-800 shadow-slate-500/10'
                        }`}>
                          {whatsappStatus === 'connected' ? (
                            <Wifi className="w-7 h-7 text-white" />
                          ) : whatsappStatus === 'connecting' ? (
                            <Loader2 className="w-7 h-7 text-white animate-spin" />
                          ) : (
                            <WifiOff className="w-7 h-7 text-white/70" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-white text-lg">
                            {whatsappStatus === 'connected' ? 'WhatsApp Conectado' 
                              : whatsappStatus === 'connecting' ? 'Conectando...' 
                              : 'Sin Conexión'}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {whatsappStatus === 'connected' 
                              ? `Número activo: ${whatsappInfo?.phone || 'Vinculado'}` 
                              : whatsappStatus === 'connecting'
                              ? 'Completando el proceso de verificación con Meta...'
                              : 'Conecta tu número de WhatsApp Business para empezar a recibir mensajes.'}
                          </p>
                        </div>
                        {whatsappStatus === 'connected' && (
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
                            <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">Activo</span>
                          </div>
                        )}
                      </div>

                      {/* Connect Button */}
                      {whatsappStatus !== 'connected' && (
                        <button
                          onClick={() => {
                            // TODO: Launch Meta Embedded Signup SDK
                            // window.FB.login(callback, { config_id: META_CONFIG_ID, ... })
                            toast.info('🔧 Este módulo requiere la aprobación de Meta Tech Provider. Configuración en progreso.');
                          }}
                          disabled={whatsappStatus === 'connecting'}
                          className="w-full flex items-center justify-center gap-3 px-8 py-5 rounded-2xl font-bold text-lg transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            background: 'linear-gradient(135deg, #25D366, #128C7E)',
                            color: '#fff',
                            boxShadow: '0 8px 32px rgba(37, 211, 102, 0.3)'
                          }}
                        >
                          <Smartphone className="w-6 h-6" />
                          Conectar WhatsApp Business
                          <ExternalLink className="w-4 h-4 opacity-60" />
                        </button>
                      )}

                      {/* Reconnect button when connected */}
                      {whatsappStatus === 'connected' && (
                        <div className="flex gap-3">
                          <button
                            onClick={() => toast.info('Reconexión en desarrollo.')}
                            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-slate-300 text-sm font-medium hover:bg-white/10 transition-all"
                          >
                            <RefreshCw className="w-4 h-4" />
                            Reconectar número
                          </button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* How it works */}
                  <Card className="glass-panel rounded-[2.5rem] border-slate-800 p-8">
                    <CardContent className="px-0 pt-0 space-y-5">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                          <Info className="w-5 h-5 text-blue-400" />
                        </div>
                        <p className="font-bold text-white text-lg font-outfit">¿Cómo Funciona?</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[
                          { step: '1', title: 'Conectar', desc: 'Haz clic en el botón y autoriza tu cuenta de Facebook Business.', icon: ExternalLink, color: '#3b82f6' },
                          { step: '2', title: 'Verificar', desc: 'Recibe un código SMS en tu número y confírmalo en la ventana de Meta.', icon: ShieldCheck, color: '#f59e0b' },
                          { step: '3', title: 'Activar', desc: 'Tu bot se activa automáticamente y comienza a recibir mensajes.', icon: Zap, color: '#25D366' },
                        ].map((item) => (
                          <div key={item.step} className="p-5 rounded-2xl bg-slate-900/50 border border-white/5 space-y-3 hover:border-white/10 transition-all">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black text-white"
                                   style={{ background: `linear-gradient(135deg, ${item.color}, transparent)` }}>
                                {item.step}
                              </div>
                              <p className="font-bold text-white text-sm">{item.title}</p>
                            </div>
                            <p className="text-xs text-slate-400 leading-relaxed">{item.desc}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Requirements info */}
                  <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10">
                    <p className="text-[11px] text-amber-200/60 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Requisitos:</strong> Necesitas una cuenta de Facebook vinculada a un portafolio comercial de Meta Business 
                        y un número de teléfono que no esté registrado en WhatsApp Business actualmente.
                      </span>
                    </p>
                  </div>
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default TenantConfigPage;
